import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';

const EditAnimeModal = ({ isOpen, anime, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    banner: '',
    poster: '',
    genres: [],
    releaseYear: ''
  });

  // Cuando el anime cambia, actualizar el formulario
  useEffect(() => {
    if (anime) {
      // Manejar género como array o string
      let genres = [];
      if (anime.genre) {
        if (typeof anime.genre === 'string') {
          // Si es string, dividirlo por comas y eliminar espacios
          genres = anime.genre.split(',').map(g => g.trim());
        } else if (Array.isArray(anime.genre)) {
          genres = anime.genre;
        }
      }

      setFormData({
        name: anime.name || '',
        description: anime.description || '',
        banner: anime.banner || '',
        poster: anime.poster || '',
        genres: genres,
        releaseYear: anime.releaseYear || ''
      });
    }
  }, [anime]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleGenreChange = (genre) => {
    setFormData(prev => {
      const newGenres = [...prev.genres];
      if (newGenres.includes(genre)) {
        return {
          ...prev,
          genres: newGenres.filter(g => g !== genre)
        };
      } else {
        return {
          ...prev,
          genres: [...newGenres, genre]
        };
      }
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validar que al menos un género esté seleccionado
    if (formData.genres.length === 0) {
      alert('Por favor, seleccione al menos un género');
      return;
    }
    
    // Convertir array de géneros a string para compatibilidad con el backend
    const animeData = {
      ...formData,
      genre: formData.genres.join(', ')
    };
    delete animeData.genres; // Eliminamos el array de géneros ya que enviamos la versión string
    
    onSave({ id: anime.id, animeData });
  };

  const genreOptions = [
    'Acción',
    'Aventura',
    'Comedia',
    'Drama',
    'Fantasía',
    'Ciencia Ficción',
    'Terror',
    'Romance',
    'Misterio',
    'Sobrenatural',
    'Deportes',
    'Música',
    'Slice of Life',
    'Isekai',
    'Josei',
    'Seinen',
    'Shonen',
    'Thriller',
    'Películas de Anime'
  ];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-gray-800 rounded-lg shadow-lg w-full max-w-md p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-white">Editar Anime</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white focus:outline-none"
          >
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-300">
              Nombre
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              className="mt-1 block w-full border border-gray-600 rounded-md shadow-sm p-2 bg-gray-700 text-white"
            />
          </div>

          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-300">
              Descripción
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows="3"
              className="mt-1 block w-full border border-gray-600 rounded-md shadow-sm p-2 bg-gray-700 text-white"
            />
          </div>

          <div>
            <label htmlFor="banner" className="block text-sm font-medium text-gray-300">
              URL del Banner
            </label>
            <input
              type="text"
              id="banner"
              name="banner"
              value={formData.banner}
              onChange={handleChange}
              placeholder="https://ejemplo.com/banner.jpg"
              className="mt-1 block w-full border border-gray-600 rounded-md shadow-sm p-2 bg-gray-700 text-white"
            />
          </div>

          <div>
            <label htmlFor="poster" className="block text-sm font-medium text-gray-300">
              URL del Póster
            </label>
            <input
              type="text"
              id="poster"
              name="poster"
              value={formData.poster}
              onChange={handleChange}
              placeholder="https://ejemplo.com/poster.jpg"
              className="mt-1 block w-full border border-gray-600 rounded-md shadow-sm p-2 bg-gray-700 text-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Géneros
            </label>
            <div className="grid grid-cols-2 gap-2 max-h-60 overflow-y-auto p-2 border border-gray-600 rounded-md bg-gray-700">
              {genreOptions.map((option) => (
                <div key={option} className="flex items-center">
                  <input
                    type="checkbox"
                    id={`genre-${option}`}
                    checked={formData.genres.includes(option)}
                    onChange={() => handleGenreChange(option)}
                    className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-600 rounded bg-gray-700"
                  />
                  <label htmlFor={`genre-${option}`} className="text-sm text-gray-300">
                    {option}
                  </label>
                </div>
              ))}
            </div>
            <p className="mt-2 text-sm text-gray-400">
              Géneros seleccionados: {formData.genres.length > 0 ? formData.genres.join(', ') : 'Ninguno'}
            </p>
          </div>

          <div>
            <label htmlFor="releaseYear" className="block text-sm font-medium text-gray-300">
              Año de Lanzamiento
            </label>
            <input
              type="number"
              id="releaseYear"
              name="releaseYear"
              value={formData.releaseYear}
              onChange={handleChange}
              required
              min={1900}
              max={new Date().getFullYear() + 5}
              className="mt-1 block w-full border border-gray-600 rounded-md shadow-sm p-2 bg-gray-700 text-white"
            />
          </div>

          <div className="flex justify-end space-x-2 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-600 rounded-md text-gray-300 hover:bg-gray-700"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 border border-transparent rounded-md font-medium text-white hover:bg-blue-700"
            >
              Guardar Cambios
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditAnimeModal;
